import { computed } from '@angular/core';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import { withAuthReducer } from './with-auth.reducer';
import { withAuthEffects } from './with-auth.effects';
import { Role } from '../../enums/role.enum';
import { OIP_ROLES, CORP_ROLES, SUB_ROLES, CLIENT_ROLES } from '../../constants/auth';
import { Permission, permissionsForRole } from '../../constants/permissions';

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withAuthReducer(),
  withAuthEffects(),
  withComputed(({
    'custom:role': customRole,
    given_name,
    family_name,
    'custom:territory_id': territoryIdAttr,
    subProxyTerritoryIds,
    awaitingNewPassword,
  }) => ({
    /** Typed role enum derived from the Cognito custom:role attribute. */
    role: computed(() => (customRole() as Role) ?? Role.TerritoryReader),

    /** Capabilities granted to the current role (ADR-015 — gate features on these). */
    permissions: computed(() => permissionsForRole(customRole())),

    /** Whether the current user accesses the territory OIP portal. */
    isOipUser: computed(() => (OIP_ROLES as readonly string[]).includes(customRole())),

    /** Whether the current user accesses the corporate/national portal. */
    isCorpUser: computed(() => (CORP_ROLES as readonly string[]).includes(customRole())),

    /** Whether the current user is a subcontractor portal user. */
    isSubUser: computed(() => (SUB_ROLES as readonly string[]).includes(customRole())),

    /** Whether the current user is a client portal user. */
    isClientUser: computed(() => (CLIENT_ROLES as readonly string[]).includes(customRole())),

    /**
     * Cross-portal grant: an OIP identity permitted to act inside `/sub` on behalf of
     * unadopted subs. Driven by the real `custom:sub_proxy_terr_ids` claim (any territory
     * grant counts); the explicit SubProxy role is kept as a fallback.
     */
    hasSubProxyGrant: computed(
      () => subProxyTerritoryIds().length > 0 || customRole() === Role.SubProxy,
    ),

    /** True while Cognito is awaiting a forced first-sign-in password change. */
    newPasswordRequired: computed(() => awaitingNewPassword()),

    /** SUPER_ADMIN or CORPORATE_WRITER — full cross-territory access, sees all territories. */
    isCorporate: computed(() =>
      customRole() === Role.CorporateWriter || customRole() === Role.SuperAdmin,
    ),

    /** SUPER_ADMIN only — the highest tier (e.g. destructive territory delete). */
    isSuperAdmin: computed(() => customRole() === Role.SuperAdmin),

    /**
     * True for any role with cross-territory visibility.
     *
     * `isNationalAccountManager` used to sit beside this and is GONE with the roles it tested
     * (vantage-docs#80). A national account manager signs in as a CORPORATE_WRITER, so this
     * predicate already covers them — and the account-manager assignment is a dashboard view
     * filter, never something to branch authorization on (ADR-015).
     */
    hasCrossTerritoryAccess: computed(() =>
      customRole() === Role.CorporateWriter ||
      customRole() === Role.SuperAdmin,
    ),

    fullName: computed(() => `${given_name()} ${family_name()}`.trim()),

    /** Territory ID from Cognito attribute — null means cross-territory access. */
    territoryId: computed(() => territoryIdAttr() || null),
  })),
  withMethods((store) => ({
    /** Capability check — the single authorization primitive (ADR-015). */
    hasPermission: (permission: Permission): boolean => store.permissions().has(permission),
    /** True if the user has at least one of the given capabilities. */
    hasAnyPermission: (...permissions: Permission[]): boolean =>
      permissions.some((p) => store.permissions().has(p)),
  })),
);
